import { AbsoluteFill, useCurrentFrame, useVideoConfig } from "remotion";
import {
  getWordStartTimes,
  msToFrames,
  removeAudioTags,
} from "../../utils/timing.ts";
import { useMemo } from "react";
import { CompositionDto } from "@repo/api/editor";
import { formatHtmlForElevenLabs } from "@repo/api/utils/elevenlabs";
import {
  DEFAULT_ASPECT_RATIOS_POSITION,
  parseActiveTextStyleToCSS,
  parseCaptionBackgroundStyleToCSS,
  parseTextStyleToCSS,
  resolveCompositionCaptionPreset,
} from "./CaptionPresets.ts";
import { RenderWords } from "./CaptionDisplayModes.tsx";
import { useFont } from "../../utils/font.ts";

type CaptionsProps = {
  text: string;
  characterStartTimes: number[];
  compositionCaptions: CompositionDto["captions"];
  aspectRatio: CompositionDto["aspectRatio"];
  isPreview?: boolean;
};

function useCaptionSections(text: string, characterStartTimes: number[]) {
  return useMemo(() => {
    const words = getWordStartTimes(
      removeAudioTags(formatHtmlForElevenLabs(text)),
      characterStartTimes,
    );

    const rawSections = words.reduce((acc: (typeof words)[], word, index) => {
      const shouldStartNewSection =
        index === 0 ||
        (words[index - 1] && /[,.!?;]$/.test(words[index - 1]!.word));

      if (shouldStartNewSection) {
        acc.push([word]);
      } else {
        acc[acc.length - 1]!.push(word);
      }
      return acc;
    }, []);

    const splitLongSection = (section: typeof words): (typeof words)[] => {
      const MAX_LENGTH = 50;
      const totalLength = section.reduce(
        (len, word) => len + word.word.length + 1,
        0,
      );

      if (section.length === 0) {
        return [];
      }

      if (totalLength <= MAX_LENGTH) {
        return [section];
      }

      if (section[0]!.word.length > MAX_LENGTH) {
        return [[section[0]!], ...splitLongSection(section.slice(1))];
      }

      let firstHalfLength = 0;
      let splitIndex = 0;
      const targetLength = totalLength / 2;

      for (let i = 0; i < section.length; i++) {
        const wordLength = section[i]!.word.length + 1;

        // Prevents all words being in the second half (infinite loop)
        if (i === 0 && wordLength > targetLength) {
          splitIndex = 1;
          break;
        }

        firstHalfLength += wordLength;
        if (firstHalfLength >= targetLength) {
          // Prevents all words being in the first half and not splitting (infinite loop)
          if (i + 1 === section.length && firstHalfLength > MAX_LENGTH) {
            splitIndex = i;
          } else {
            splitIndex = i + 1;
          }

          break;
        }
      }

      const firstHalf = section.slice(0, splitIndex);
      const secondHalf = section.slice(splitIndex);

      return [...splitLongSection(firstHalf), ...splitLongSection(secondHalf)];
    };

    return rawSections.flatMap(splitLongSection);
  }, [text, characterStartTimes]);
}

export function Captions({
  text,
  characterStartTimes,
  compositionCaptions,
  isPreview = false,
  aspectRatio,
}: CaptionsProps) {
  const { fps } = useVideoConfig();
  const currentFrame = useCurrentFrame();
  const currentTime = currentFrame / fps;

  const sections = useCaptionSections(text, characterStartTimes);

  let currentSectionIndex: number | null = null;
  for (let i = 0; i < sections.length; i++) {
    const section = sections[i];
    if (currentTime >= section![0]!.startTime) {
      currentSectionIndex = i;
    }
  }

  if (currentSectionIndex === null) {
    return null;
  }

  return (
    <AbsoluteFill
      style={{
        height: "100%",
        width: "100%",
        pointerEvents: "none",
      }}
    >
      <CaptionSection
        key={currentSectionIndex}
        currentSection={sections[currentSectionIndex]!}
        compositionCaptions={compositionCaptions}
        isPreview={isPreview}
        aspectRatio={aspectRatio}
      />
    </AbsoluteFill>
  );
}

export type CurrentSection = {
  word: string;
  startTime: number;
};

type CaptionSectionProps = {
  currentSection: CurrentSection[];
  compositionCaptions: CompositionDto["captions"];
  isPreview?: boolean;
  aspectRatio: CompositionDto["aspectRatio"];
};

function CaptionSection({
  currentSection,
  compositionCaptions,
  isPreview = false,
  aspectRatio,
}: CaptionSectionProps) {
  const { width, fps } = useVideoConfig();
  const currentFrame = useCurrentFrame();

  const resolvedCompositionCaption =
    resolveCompositionCaptionPreset(compositionCaptions);

  const loadedFontFamily = useFont({
    fontExternalId:
      resolvedCompositionCaption?.config?.style?.text?.fontExternalId,
    fontWeight: resolvedCompositionCaption?.config?.style?.text?.fontWeight,
    fontStyle: resolvedCompositionCaption?.config?.style?.text?.fontStyle,
  });

  if (currentSection.length === 0) {
    return null;
  }

  if (!resolvedCompositionCaption) {
    return null;
  }

  const lastWord = currentSection[currentSection.length - 1]!;
  const lastWordEndTimeMs = (lastWord.startTime + 2) * 1000;

  if (currentFrame > msToFrames(lastWordEndTimeMs, fps)) {
    return null;
  }

  const { style, displayMode } = resolvedCompositionCaption.config;

  const paddingHorizontal = `${width * 0.1}px`;

  const fontSize = style.text?.fontSize
    ? isPreview
      ? Math.min(style.text.fontSize, width * 0.06)
      : style.text.fontSize
    : undefined;

  const previewScale =
    isPreview && style.text?.fontSize
      ? Math.min(1, (width * 0.08) / style.text.fontSize)
      : 1;

  const yPosition =
    style?.position?.[aspectRatio]?.y ??
    DEFAULT_ASPECT_RATIOS_POSITION[aspectRatio]!.y;
  const topPercent = 100 - yPosition;

  return (
    <div
      className="relative h-full w-full"
      style={{
        opacity: style.opacity != null ? style.opacity / 100 : undefined,
      }}
    >
      <div
        className="absolute left-0 right-0 flex justify-center"
        style={{
          top: isPreview ? "50%" : `${topPercent}%`,
          transform: isPreview
            ? "translateY(-50%)"
            : `translateY(-${topPercent}%)`,
          paddingLeft: isPreview ? "12px" : paddingHorizontal,
          paddingRight: isPreview ? "12px" : paddingHorizontal,
        }}
      >
        <RenderWords
          displayMode={displayMode}
          currentSection={currentSection}
          textStyles={{
            ...parseTextStyleToCSS(style.text),
            fontSize,
            fontFamily: loadedFontFamily ?? style.text?.fontFamily,
          }}
          activeTextStyles={parseActiveTextStyleToCSS(style.activeText)}
          animation={style.animation}
          containerStyle={{
            ...parseCaptionBackgroundStyleToCSS(style.background, previewScale),
            whiteSpace: isPreview ? "nowrap" : "normal",
            maxWidth: isPreview ? undefined : `${width * 0.8}px`,
            textWrap: "balance",
          }}
        />
      </div>
    </div>
  );
}
